/**
 * Frame Scheduler - Manages frame rate limiting for detection loops
 *
 * Uses requestAnimationFrame with intelligent frame skipping:
 * - Target FPS maintained regardless of display refresh rate (60Hz, 120Hz, 144Hz, etc.)
 * - If GPU is slow, runs at maximum achievable FPS (natural throttling)
 * - No backlog/catch-up behavior when GPU speeds up after being slow
 * - 10% tolerance to handle rAF timing jitter across all refresh rates
 */

/**
 * Determines if a frame should be processed based on elapsed time
 *
 * @param {number} currentTime - Current timestamp (ms)
 * @param {number} lastRunTime - Timestamp of last processed frame (ms)
 * @param {number} targetFPS - Target frames per second
 * @param {boolean} maxFPS - If true, skip rate limiting
 * @returns {{ shouldRun: boolean, effectiveInterval: number }}
 */
export function shouldProcessFrame(currentTime, lastRunTime, targetFPS, maxFPS = false) {
  const targetInterval = 1000 / targetFPS;
  // 10% tolerance handles rAF jitter at any display refresh rate
  // Use floor to avoid floating point precision issues
  const effectiveInterval = Math.floor(targetInterval * 0.9);

  if (maxFPS) {
    return { shouldRun: true, effectiveInterval };
  }

  const elapsed = currentTime - lastRunTime;
  return {
    shouldRun: elapsed >= effectiveInterval,
    effectiveInterval
  };
}

/**
 * Simulates frame scheduling over time for testing
 *
 * @param {Object} options
 * @param {number} options.displayHz - Display refresh rate (e.g., 60, 120, 144)
 * @param {number} options.targetFPS - Target FPS for detection
 * @param {number} options.durationMs - Simulation duration in milliseconds
 * @param {number} options.inferenceTimeMs - Time each inference takes (default: 0)
 * @param {boolean} options.maxFPS - Disable rate limiting (default: false)
 * @param {number[]} options.inferenceTimesMs - Variable inference times per frame (overrides inferenceTimeMs)
 * @returns {{ frameTimestamps: number[], inferenceCount: number, actualFPS: number }}
 */
export function simulateFrameScheduling(options) {
  const {
    displayHz,
    targetFPS,
    durationMs,
    inferenceTimeMs = 0,
    maxFPS = false,
    inferenceTimesMs = null
  } = options;

  const frameInterval = 1000 / displayHz;
  const frameTimestamps = []; // Timestamps when inference actually ran
  // Initialize lastRunTime to negative infinity so first frame always runs
  // (simulates real rAF where timestamp is time since page load, not 0)
  let lastRunTime = -Infinity;
  let isRunning = false;
  let inferenceEndTime = 0;
  let inferenceIndex = 0;

  // Simulate rAF callbacks starting at time 0
  for (let timestamp = 0; timestamp <= durationMs; timestamp += frameInterval) {
    // Check if previous inference is still running
    if (isRunning && timestamp < inferenceEndTime) {
      continue;
    }
    isRunning = false;

    // Check if we should process this frame
    const { shouldRun } = shouldProcessFrame(timestamp, lastRunTime, targetFPS, maxFPS);

    if (shouldRun) {
      frameTimestamps.push(timestamp);
      lastRunTime = timestamp;
      isRunning = true;

      // Determine inference time for this frame
      const currentInferenceTime = inferenceTimesMs
        ? inferenceTimesMs[inferenceIndex % inferenceTimesMs.length]
        : inferenceTimeMs;
      inferenceEndTime = timestamp + currentInferenceTime;
      inferenceIndex++;
    }
  }

  const inferenceCount = frameTimestamps.length;
  // Calculate FPS based on how many complete frames fit in the duration
  // Use the last frame timestamp to get actual elapsed time
  const lastTimestamp = frameTimestamps.length > 0 ? frameTimestamps[frameTimestamps.length - 1] : 0;
  const actualFPS = inferenceCount > 1 && lastTimestamp > 0
    ? ((inferenceCount - 1) / lastTimestamp) * 1000
    : 0;

  return {
    frameTimestamps,
    inferenceCount,
    actualFPS
  };
}

/**
 * Creates a frame scheduler for use with requestAnimationFrame
 *
 * @param {Object} options
 * @param {number} options.targetFPS - Target frames per second
 * @param {boolean} options.maxFPS - Disable rate limiting
 * @param {Function} options.onFrame - Callback when frame should be processed
 * @param {Function} options.requestAnimationFrame - rAF function (for testing)
 * @param {Function} options.cancelAnimationFrame - cancelRAF function (for testing)
 * @returns {{ start: Function, stop: Function }}
 */
export function createFrameScheduler(options) {
  const {
    targetFPS,
    maxFPS = false,
    onFrame,
    requestAnimationFrame: raf = globalThis.requestAnimationFrame,
    cancelAnimationFrame: caf = globalThis.cancelAnimationFrame
  } = options;

  let stopped = false;
  let rafId = null;
  let lastRunTime = 0;
  let isRunning = false;

  const loop = async (timestamp) => {
    if (stopped) return;

    // Schedule next frame immediately
    rafId = raf(loop);

    // Skip if previous frame still processing
    if (isRunning) return;

    // Check if we should process this frame
    const { shouldRun } = shouldProcessFrame(timestamp, lastRunTime, targetFPS, maxFPS);
    if (!shouldRun) return;

    // Process frame
    isRunning = true;
    lastRunTime = timestamp;

    try {
      await onFrame(timestamp);
    } finally {
      isRunning = false;
    }
  };

  return {
    start() {
      stopped = false;
      rafId = raf(loop);
    },
    stop() {
      stopped = true;
      if (rafId !== null) {
        caf(rafId);
        rafId = null;
      }
    }
  };
}
